import { ClienteStats } from '../../types';
import { formatCurrency } from '../../utils/constants';
import {
  DocumentTextIcon,
  CurrencyEuroIcon,
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  ChartBarSquareIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

interface ClientKPICardsProps {
  stats?: ClienteStats;
  isLoading: boolean;
}

const SkeletonCard = () => (
  <div className="bg-white p-4 rounded-lg shadow">
    <div className="flex items-center">
      <div className="flex-shrink-0">
        <div className="animate-pulse bg-gray-200 h-8 w-8 rounded" />
      </div>
      <div className="ml-3 space-y-2 flex-1">
        <div className="animate-pulse bg-gray-200 h-3 w-20 rounded" />
        <div className="animate-pulse bg-gray-200 h-6 w-16 rounded" />
      </div>
    </div>
  </div>
);

function conversionColor(rate: number): string {
  if (rate >= 50) return 'text-green-600';
  if (rate >= 25) return 'text-yellow-600';
  return 'text-red-600';
}

function contactDaysColor(days: number | undefined): string {
  if (days === undefined) return 'text-gray-500';
  if (days < 7) return 'text-green-600';
  if (days <= 30) return 'text-yellow-600';
  return 'text-red-600';
}

export default function ClientKPICards({ stats, isLoading }: ClientKPICardsProps) {
  if (isLoading || !stats) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  const cards = [
    {
      label: 'Totale Offerte',
      value: stats.totale_offerte.toString(),
      icon: <DocumentTextIcon className="h-8 w-8 text-gray-400" />,
      valueClass: 'text-gray-900',
    },
    {
      label: 'Valore Totale',
      value: formatCurrency(stats.valore_totale),
      icon: <CurrencyEuroIcon className="h-8 w-8 text-blue-400" />,
      valueClass: 'text-gray-900',
    },
    {
      label: 'Tasso Conversione',
      value: `${stats.tasso_conversione.toFixed(1)}%`,
      icon: <ArrowTrendingUpIcon className="h-8 w-8 text-emerald-400" />,
      valueClass: conversionColor(stats.tasso_conversione),
    },
    {
      label: 'Valore Accettate',
      value: formatCurrency(stats.valore_accettate),
      icon: <CheckCircleIcon className="h-8 w-8 text-green-400" />,
      valueClass: 'text-gray-900',
    },
    {
      label: 'Importo Medio',
      value: formatCurrency(stats.importo_medio),
      icon: <ChartBarSquareIcon className="h-8 w-8 text-blue-400" />,
      valueClass: 'text-gray-900',
    },
    {
      label: 'Ultimo Contatto',
      value:
        stats.giorni_da_ultimo_contatto !== undefined && stats.giorni_da_ultimo_contatto !== null
          ? `${stats.giorni_da_ultimo_contatto} giorni fa`
          : 'N/D',
      icon: <ClockIcon className="h-8 w-8 text-orange-400" />,
      valueClass: contactDaysColor(stats.giorni_da_ultimo_contatto),
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {cards.map((card) => (
        <div key={card.label} className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">{card.icon}</div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-500">{card.label}</p>
              <p className={`text-lg font-semibold ${card.valueClass}`}>{card.value}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
